import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import useCurrentUserAccess from "../../hooks/useCurrentUserAccess";
import "./WorkflowEditor.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://agent-book-crm-backend.onrender.com";

const defaultTriggers = {
  bookmeAppointmentCreated: { enabled: false },
  recordType: { enabled: false, values: [] },
  coverageType: { enabled: false, value: "" },
  taggedWith: { enabled: false, value: "" },
  status: { enabled: false, value: "" },
  leadSource: { enabled: false, value: "" },
  carrier: { enabled: false, value: "" },
  product: { enabled: false, value: "" },
  opportunityStatus: { enabled: false, value: "" },
  policyStatus: { enabled: false, value: "" },
  zipCode: { enabled: false, value: "" },
  onDate: {
    enabled: false,
    days: "",
    direction: "on",
    dateType: "",
  },
};

const createEmptyAction = (emailRecipientOptions = []) => ({
  action_type: "",
  offset_amount: "",
  offset_unit: "days",
  offset_direction: "after_previous",
  send_to: emailRecipientOptions[0]?.value || "record",
  email_template_id: "",
  sms_template_id: "",
  task_title: "",
  task_description: "",
  tag: "",
  status: "",
});

function TriggerDropdown({
  label,
  triggerKey,
  options,
  triggers,
  toggleTrigger,
  updateValue,
}) {
  return (
    <div className="workflow-row">
      <div className="workflow-row-check">
        <input
          type="checkbox"
          checked={triggers[triggerKey].enabled}
          onChange={() => toggleTrigger(triggerKey)}
        />
      </div>

      <div className="workflow-row-label">{label}</div>

      <div className="workflow-row-control">
        <select
          className="workflow-input"
          disabled={!triggers[triggerKey].enabled}
          value={triggers[triggerKey].value}
          onChange={(e) => updateValue(triggerKey, "value", e.target.value)}
        >
          <option value="">Select {label}</option>
          {(Array.isArray(options) ? options : []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default function WorkflowEditor() {
  const { id } = useParams();
  const {
    loading: accessLoading,
    platformRole,
    accountRole,
  } = useCurrentUserAccess();

  const hostname = window.location.hostname.toLowerCase();
  const isDemoSubdomain =
    hostname !== "agentbookcrm.com" &&
    hostname !== "www.agentbookcrm.com" &&
    hostname.endsWith(".agentbookcrm.com") &&
    !hostname.startsWith("app.");

  const isPlatformAdmin = platformRole?.key_name === "platform_admin";
  const isPlatformEmployee = platformRole?.key_name === "platform_employee";
  const isAccountAdmin = accountRole?.key_name === "account_admin";
  const isAccountUser = accountRole?.key_name === "account_user";

  const isPlatformSide =
    !isDemoSubdomain && (isPlatformAdmin || isPlatformEmployee);
  const isAccountSide = isDemoSubdomain || isAccountAdmin || isAccountUser;

  const workflowContext = isPlatformSide ? "platform" : "subscriber";

  const [workflowName, setWorkflowName] = useState("");
  const [saving, setSaving] = useState(false);
  const [workflowId, setWorkflowId] = useState(id || null);
  const [actions, setActions] = useState([]);

  const [tagOptions, setTagOptions] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [coverageTypeOptions, setCoverageTypeOptions] = useState([]);
  const [policyStatusOptions, setPolicyStatusOptions] = useState([]);
  const [opportunityStatusOptions, setOpportunityStatusOptions] = useState([]);
  const [emailTemplateOptions, setEmailTemplateOptions] = useState([]);

  const emailRecipientOptions = useMemo(() => {
    return workflowContext === "platform"
      ? [
          { value: "record", label: "Record" },
          { value: "platform_user", label: "Platform User" },
        ]
      : [
          { value: "record", label: "Record" },
          { value: "agent", label: "Agent" },
        ];
  }, [workflowContext]);

  const recordTypeOptions = useMemo(() => {
    return workflowContext === "platform"
      ? ["Lead", "Subscriber"]
      : ["Lead", "Client"];
  }, [workflowContext]);

  const [editingActionId, setEditingActionId] = useState(null);
  const [newAction, setNewAction] = useState(
    createEmptyAction(emailRecipientOptions)
  );
  const [triggers, setTriggers] = useState(defaultTriggers);

  useEffect(() => {
    setNewAction((prev) => {
      if (emailRecipientOptions.some((opt) => opt.value === prev.send_to)) {
        return prev;
      }

      return {
        ...prev,
        send_to: emailRecipientOptions[0]?.value || "record",
      };
    });
  }, [emailRecipientOptions]);

  useEffect(() => {
    setTriggers((prev) => {
      const currentValues = Array.isArray(prev.recordType.values)
        ? prev.recordType.values
        : [];

      const filteredValues = currentValues.filter((value) =>
        recordTypeOptions.includes(value)
      );

      if (filteredValues.length === currentValues.length) {
        return prev;
      }

      return {
        ...prev,
        recordType: {
          ...prev.recordType,
          values: filteredValues,
        },
      };
    });
  }, [recordTypeOptions]);

  useEffect(() => {
    if (!id) return;

    const loadWorkflow = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_BASE_URL}/api/automations/workflows/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!res.ok) {
          throw new Error("Failed to load workflow");
        }

        const data = await res.json();

        setWorkflowName(data.name || "");
        setWorkflowId(data.id);
        setActions(Array.isArray(data.actions) ? data.actions : []);

        const updatedTriggers = {
          ...defaultTriggers,
          recordType: { ...defaultTriggers.recordType },
          onDate: { ...defaultTriggers.onDate },
        };

        if (Array.isArray(data.triggers)) {
          data.triggers.forEach((trigger) => {
            if (!updatedTriggers[trigger.type]) return;

            updatedTriggers[trigger.type] = {
              ...updatedTriggers[trigger.type],
              enabled: true,
              ...trigger.config,
            };
          });
        }

        setTriggers(updatedTriggers);
      } catch (err) {
        console.error("❌ Load workflow error:", err);
      }
    };

    loadWorkflow();
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    async function loadOptions() {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        const [
          tagsRes,
          leadSourcesRes,
          carriersRes,
          coverageTypesRes,
          policyStatusRes,
          opportunityStatusRes,
          leadStatusesRes,
          emailTemplatesRes,
        ] = await Promise.all([
          fetch(`${API_BASE_URL}/api/tags`, { headers }),
          fetch(`${API_BASE_URL}/api/lead-sources`, { headers }),
          fetch(`${API_BASE_URL}/api/carriers`, { headers }),
          fetch(`${API_BASE_URL}/api/coverage-types`, { headers }),
          fetch(`${API_BASE_URL}/api/policy-statuses?type=policy`, {
            headers,
          }),
          fetch(`${API_BASE_URL}/api/policy-statuses?type=opportunity`, {
            headers,
          }),
          fetch(`${API_BASE_URL}/api/lead-statuses`, { headers }),
          fetch(`${API_BASE_URL}/api/templates/email`, { headers }),
        ]);

        const tags = await tagsRes.json();
        const leadSources = await leadSourcesRes.json();
        const carriers = await carriersRes.json();
        const coverageTypes = await coverageTypesRes.json();
        const policyStatuses = await policyStatusRes.json();
        const opportunityStatuses = await opportunityStatusRes.json();
        const leadStatuses = await leadStatusesRes.json();
        const emailTemplates = await emailTemplatesRes.json();

        setTagOptions(Array.isArray(tags) ? tags.map((t) => t.name) : []);
        setLeadSourceOptions(
          Array.isArray(leadSources) ? leadSources.map((s) => s.name) : []
        );
        setCarrierOptions(
          Array.isArray(carriers) ? carriers.map((c) => c.name) : []
        );
        setCoverageTypeOptions(
          Array.isArray(coverageTypes)
            ? coverageTypes.map((c) => c.name)
            : []
        );
        setPolicyStatusOptions(
          Array.isArray(policyStatuses)
            ? policyStatuses.map((s) => s.name)
            : []
        );
        setOpportunityStatusOptions(
          Array.isArray(opportunityStatuses)
            ? opportunityStatuses.map((s) => s.name)
            : []
        );
        setLeadStatusOptions(
          Array.isArray(leadStatuses) ? leadStatuses.map((s) => s.name) : []
        );
        setEmailTemplateOptions(
          Array.isArray(emailTemplates) ? emailTemplates : []
        );
      } catch (err) {
        console.error("❌ Failed loading workflow options:", err);
      }
    }

    loadOptions();
  }, []);

  const toggleTrigger = (key) => {
    setTriggers((prev) => {
      const nextEnabled = !prev[key].enabled;

      if (key === "recordType" && nextEnabled && prev.recordType.values.length === 0) {
        return {
          ...prev,
          recordType: {
            ...prev.recordType,
            enabled: true,
            values:
              workflowContext === "platform"
                ? ["Lead"]
                : ["Lead", "Client"],
          },
        };
      }

      if (key === "onDate" && nextEnabled) {
        return {
          ...prev,
          onDate: {
            ...prev.onDate,
            enabled: true,
            direction: prev.onDate.direction || "on",
          },
        };
      }

      return {
        ...prev,
        [key]: { ...prev[key], enabled: nextEnabled },
      };
    });
  };

  const updateValue = (key, field, value) => {
    setTriggers((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const getRecordTypeDropdownValue = () => {
    if (workflowContext === "platform") {
      return triggers.recordType.values.includes("Subscriber")
        ? "subscriber"
        : "lead";
    }

    if (
      triggers.recordType.values.includes("Lead") &&
      triggers.recordType.values.includes("Client")
    ) {
      return "both";
    }

    if (triggers.recordType.values.includes("Client")) {
      return "client";
    }

    return "lead";
  };

  const handleRecordTypeDropdownChange = (value) => {
    if (workflowContext === "platform") {
      updateValue(
        "recordType",
        "values",
        value === "subscriber" ? ["Subscriber"] : ["Lead"]
      );
      return;
    }

    if (value === "both") {
      updateValue("recordType", "values", ["Lead", "Client"]);
    } else if (value === "client") {
      updateValue("recordType", "values", ["Client"]);
    } else {
      updateValue("recordType", "values", ["Lead"]);
    }
  };

  const handleOnDateDirectionChange = (value) => {
    updateValue("onDate", "direction", value);

    if (value === "on") {
      updateValue("onDate", "days", "");
    }
  };

  const resetActionForm = () => {
    setEditingActionId(null);
    setNewAction(createEmptyAction(emailRecipientOptions));
  };

  const handleSaveWorkflow = async () => {
    if (!workflowName.trim()) {
      alert("Workflow name is required.");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token");
      if (!token) {
        alert("You are not authenticated.");
        return;
      }

      const normalizedTriggers = [];

      Object.keys(triggers).forEach((key) => {
        const trigger = triggers[key];
        if (!trigger.enabled) return;

        normalizedTriggers.push({
          type: key,
          config: trigger,
        });
      });

      const payload = {
        name: workflowName,
        triggers: normalizedTriggers,
        actions,
      };

      const method = workflowId ? "PUT" : "POST";
      const url = workflowId
        ? `${API_BASE_URL}/api/automations/workflows/${workflowId}`
        : `${API_BASE_URL}/api/automations/workflows`;

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ Workflow save failed:", res.status, errorText);
        alert(`Failed to save workflow (${res.status}).`);
        return;
      }

      let data = {};
      try {
        data = await res.json();
      } catch (err) {
        console.warn("⚠️ No JSON returned from server.");
      }

      if (!workflowId) {
        setWorkflowId(data.workflow?.id || data.id);
      }

      alert("Workflow saved successfully!");
    } catch (err) {
      console.error("❌ Unexpected save error:", err);
      alert("Unexpected error while saving workflow.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddActionInline = () => {
    if (!newAction.action_type) {
      alert("Select an action type.");
      return;
    }

    if (
      newAction.action_type === "send_email" &&
      !newAction.email_template_id
    ) {
      alert("Select an email template.");
      return;
    }

    if (
      newAction.action_type === "create_task" &&
      !newAction.task_title.trim()
    ) {
      alert("Task title is required.");
      return;
    }

    if (
      (newAction.action_type === "add_tag" ||
        newAction.action_type === "remove_tag") &&
      !newAction.tag
    ) {
      alert("Select a tag.");
      return;
    }

    if (newAction.action_type === "update_status" && !newAction.status) {
      alert("Select a status.");
      return;
    }

    if (editingActionId) {
      setActions((prev) =>
        prev.map((a) =>
          a.id === editingActionId
            ? {
                ...a,
                ...newAction,
                step_number: a.step_number,
              }
            : a
        )
      );
    } else {
      const newStep = {
        id: Date.now(),
        ...newAction,
        step_number: actions.length + 1,
      };

      setActions((prev) =>
        [...prev, newStep].map((a, index) => ({
          ...a,
          step_number: index + 1,
        }))
      );
    }

    resetActionForm();
  };

  const handleEditAction = (action) => {
    setEditingActionId(action.id);
    setNewAction({
      action_type: action.action_type || "",
      offset_amount: action.offset_amount || "",
      offset_unit: action.offset_unit || "days",
      offset_direction: action.offset_direction || "after_previous",
      send_to: action.send_to || emailRecipientOptions[0]?.value || "record",
      email_template_id: action.email_template_id || "",
      sms_template_id: action.sms_template_id || "",
      task_title: action.task_title || "",
      task_description: action.task_description || "",
      tag: action.tag || "",
      status: action.status || "",
    });
  };

  const handleDeleteAction = (actionId) => {
    setActions((prev) =>
      prev
        .filter((a) => a.id !== actionId)
        .map((a, index) => ({
          ...a,
          step_number: index + 1,
        }))
    );
  };

  const getActionSummary = (action) => {
    if (action.action_type === "send_email") {
      const template = emailTemplateOptions.find(
        (t) => String(t.id) === String(action.email_template_id)
      );
      return `${action.send_to || "record"} · ${
        template?.name || "No template selected"
      }`;
    }

    if (action.action_type === "send_sms") {
      return `SMS template ${action.sms_template_id || "not selected"}`;
    }

    if (action.action_type === "create_task") {
      return action.task_title || "Task";
    }

    if (
      action.action_type === "add_tag" ||
      action.action_type === "remove_tag"
    ) {
      return action.tag || "Tag not selected";
    }

    if (action.action_type === "update_status") {
      return action.status || "Status not selected";
    }

    return "";
  };

  const triggerCount = Object.values(triggers).filter((t) => t.enabled).length;
  const actionCount = actions.length;

  if (accessLoading) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page workflow-page">
          <div className="sales-section-card">Loading workflow editor...</div>
        </div>
      </>
    );
  }

  if (!isPlatformSide && !isAccountSide) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page workflow-page">
          <div className="sales-section-card">
            Unable to determine workflow context for this user.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page workflow-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">
              {workflowName || (workflowId ? "Edit Workflow" : "Create Workflow")}
            </h1>
            <p className="sales-subtitle">
              Build workflow triggers and actions in the same clean flow
              as the rest of your CRM.
            </p>

            <div className="sales-hero-meta">
              <span className="sales-chip sales-chip-neutral">
                {workflowContext === "platform"
                  ? "Platform Workflow"
                  : "Subscriber Workflow"}
              </span>
              <span className="sales-chip sales-chip-outline">
                {triggerCount} Trigger{triggerCount === 1 ? "" : "s"}
              </span>
              <span className="sales-chip sales-chip-outline">
                {actionCount} Action{actionCount === 1 ? "" : "s"}
              </span>
            </div>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-primary-btn"
              onClick={handleSaveWorkflow}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Workflow"}
            </button>
          </div>
        </div>

        <div style={{ marginTop: "12px", marginBottom: "12px" }}>
          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Workflow Name</h2>
              <p>Give this workflow a clear internal name.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Name</label>
                <input
                  type="text"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="Enter workflow name"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="sales-profile-grid">
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Triggers</h2>
              <p>Choose what conditions should start this workflow.</p>
            </div>

            <div className="sales-form-fields workflow-fields">
              {workflowContext === "subscriber" && (
                <div className="workflow-row workflow-row-top">
                  <div className="workflow-row-check">
                    <input
                      type="checkbox"
                      checked={triggers.bookmeAppointmentCreated.enabled}
                      onChange={() => toggleTrigger("bookmeAppointmentCreated")}
                    />
                  </div>

                  <div className="workflow-row-label">
                    BookMe Appointment Created
                  </div>

                  <div className="workflow-row-control">
                    <div className="workflow-help-text">
                      Starts when someone books a meeting through a BookMe public
                      booking page.
                    </div>
                  </div>
                </div>
              )}

              <div className="workflow-row">
                <div className="workflow-row-check">
                  <input
                    type="checkbox"
                    checked={triggers.recordType.enabled}
                    onChange={() => toggleTrigger("recordType")}
                  />
                </div>

                <div className="workflow-row-label">Apply To</div>

                <div className="workflow-row-control">
                  <select
                    className="workflow-input"
                    disabled={!triggers.recordType.enabled}
                    value={getRecordTypeDropdownValue()}
                    onChange={(e) => handleRecordTypeDropdownChange(e.target.value)}
                  >
                    {workflowContext === "platform" ? (
                      <>
                        <option value="lead">Sales Leads only</option>
                        <option value="subscriber">Subscribers only</option>
                      </>
                    ) : (
                      <>
                        <option value="lead">Leads only</option>
                        <option value="client">Clients only</option>
                        <option value="both">Leads and Clients</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <TriggerDropdown
                label="Coverage Type"
                triggerKey="coverageType"
                options={coverageTypeOptions}
                {...{ triggers, toggleTrigger, updateValue }}
              />
              <TriggerDropdown
                label="Tagged With"
                triggerKey="taggedWith"
                options={tagOptions}
                {...{ triggers, toggleTrigger, updateValue }}
              />
              <TriggerDropdown
                label="Status"
                triggerKey="status"
                options={leadStatusOptions}
                {...{ triggers, toggleTrigger, updateValue }}
              />
              <TriggerDropdown
                label="Lead Source"
                triggerKey="leadSource"
                options={leadSourceOptions}
                {...{ triggers, toggleTrigger, updateValue }}
              />
              <TriggerDropdown
                label="Carrier"
                triggerKey="carrier"
                options={carrierOptions}
                {...{ triggers, toggleTrigger, updateValue }}
              />
              <TriggerDropdown
                label="Product"
                triggerKey="product"
                options={["Plan A", "Plan B"]}
                {...{ triggers, toggleTrigger, updateValue }}
              />
              <TriggerDropdown
                label="Opportunity Status"
                triggerKey="opportunityStatus"
                options={opportunityStatusOptions}
                {...{ triggers, toggleTrigger, updateValue }}
              />
              <TriggerDropdown
                label="Policy Status"
                triggerKey="policyStatus"
                options={policyStatusOptions}
                {...{ triggers, toggleTrigger, updateValue }}
              />
              <TriggerDropdown
                label="Zip Code"
                triggerKey="zipCode"
                options={["12345", "90210"]}
                {...{ triggers, toggleTrigger, updateValue }}
              />

              <div className="workflow-row workflow-row-top">
                <div className="workflow-row-check">
                  <input
                    type="checkbox"
                    checked={triggers.onDate.enabled}
                    onChange={() => toggleTrigger("onDate")}
                  />
                </div>

                <div className="workflow-row-label">On Date</div>

                <div className="workflow-row-control">
                  <div className="workflow-inline-group">
                    <select
                      className="workflow-input small"
                      disabled={!triggers.onDate.enabled}
                      value={triggers.onDate.direction || "on"}
                      onChange={(e) => handleOnDateDirectionChange(e.target.value)}
                    >
                      <option value="on">On</option>
                      <option value="before">Before</option>
                      <option value="after">After</option>
                    </select>

                    {triggers.onDate.direction !== "on" && (
                      <input
                        type="number"
                        placeholder="Days"
                        className="workflow-input small"
                        disabled={!triggers.onDate.enabled}
                        value={triggers.onDate.days}
                        onChange={(e) =>
                          updateValue("onDate", "days", e.target.value)
                        }
                      />
                    )}

                    <select
                      className="workflow-input"
                      disabled={!triggers.onDate.enabled}
                      value={triggers.onDate.dateType}
                      onChange={(e) =>
                        updateValue("onDate", "dateType", e.target.value)
                      }
                    >
                      <option value="">Select trigger date</option>
                      <option value="birthdate">Birthday</option>
                      <option value="turns_65">Turns 65</option>
                      <option value="turns_26">Turns 26</option>
                      <option value="policy_effective">
                        Policy Effective Date
                      </option>
                      <option value="policy_application">
                        Policy Application Date
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <div>
                <h2>{editingActionId ? "Edit Action" : "Add Action"}</h2>
                <p>Build the next step in this workflow.</p>
              </div>

              {editingActionId && (
                <div className="sales-header-actions">
                  <button
                    className="sales-secondary-btn"
                    onClick={resetActionForm}
                  >
                    Cancel Editing
                  </button>
                </div>
              )}
            </div>

            <div className="sales-form-fields workflow-fields">
              <div className="sales-field">
                <label>Action Type</label>
                <select
                  value={newAction.action_type || ""}
                  onChange={(e) => {
                    const actionType = e.target.value;
                    setEditingActionId(null);
                    setNewAction({
                      ...createEmptyAction(emailRecipientOptions),
                      action_type: actionType,
                    });
                  }}
                >
                  <option value="">Select Action</option>
                  <option value="send_email">Send Email</option>
                  <option value="send_sms">Send SMS</option>
                  <option value="create_task">Create Task</option>
                  <option value="add_tag">Add Tag</option>
                  <option value="remove_tag">Remove Tag</option>
                  <option value="update_status">Update Status</option>
                </select>
              </div>

              {newAction.action_type === "send_email" && (
                <>
                  <div className="sales-field">
                    <label>Due In</label>
                    <div className="workflow-inline-group">
                      <input
                        type="number"
                        placeholder="0"
                        className="workflow-input small"
                        value={newAction.offset_amount}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_amount: e.target.value,
                          })
                        }
                      />
                      <select
                        className="workflow-input small"
                        value={newAction.offset_unit}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_unit: e.target.value,
                          })
                        }
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                  </div>

                  <div className="sales-field">
                    <label>Timing Reference</label>
                    <select
                      value={newAction.offset_direction}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          offset_direction: e.target.value,
                        })
                      }
                    >
                      <option value="after_previous">
                        After Previous Action
                      </option>
                      <option value="after">After Trigger</option>
                      <option value="before">Before Trigger</option>
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>Send To</label>
                    <select
                      value={newAction.send_to}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          send_to: e.target.value,
                        })
                      }
                    >
                      {emailRecipientOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Select Template</label>
                    <select
                      value={newAction.email_template_id}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          email_template_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Template</option>
                      {emailTemplateOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — {t.subject}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {newAction.action_type === "send_sms" && (
                <>
                  <div className="sales-field">
                    <label>Due In</label>
                    <div className="workflow-inline-group">
                      <input
                        type="number"
                        placeholder="0"
                        className="workflow-input small"
                        value={newAction.offset_amount}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_amount: e.target.value,
                          })
                        }
                      />
                      <select
                        className="workflow-input small"
                        value={newAction.offset_unit}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_unit: e.target.value,
                          })
                        }
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                  </div>

                  <div className="sales-field">
                    <label>Timing Reference</label>
                    <select
                      value={newAction.offset_direction}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          offset_direction: e.target.value,
                        })
                      }
                    >
                      <option value="after_previous">
                        After Previous Action
                      </option>
                      <option value="after">After Trigger</option>
                      <option value="before">Before Trigger</option>
                    </select>
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Select Template</label>
                    <select
                      value={newAction.sms_template_id}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          sms_template_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Template</option>
                    </select>
                  </div>
                </>
              )}

              {newAction.action_type === "create_task" && (
                <>
                  <div className="sales-field">
                    <label>Due In</label>
                    <div className="workflow-inline-group">
                      <input
                        type="number"
                        placeholder="0"
                        className="workflow-input small"
                        value={newAction.offset_amount}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_amount: e.target.value,
                          })
                        }
                      />
                      <select
                        className="workflow-input small"
                        value={newAction.offset_unit}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_unit: e.target.value,
                          })
                        }
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                  </div>

                  <div className="sales-field">
                    <label>Timing Reference</label>
                    <select
                      value={newAction.offset_direction}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          offset_direction: e.target.value,
                        })
                      }
                    >
                      <option value="after_previous">
                        After Previous Action
                      </option>
                      <option value="after">After Trigger</option>
                      <option value="before">Before Trigger</option>
                    </select>
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Task Title</label>
                    <input
                      value={newAction.task_title}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          task_title: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Task Description</label>
                    <textarea
                      className="workflow-textarea-native"
                      value={newAction.task_description}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          task_description: e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              )}

              {(newAction.action_type === "add_tag" ||
                newAction.action_type === "remove_tag") && (
                <>
                  <div className="sales-field">
                    <label>Due In</label>
                    <div className="workflow-inline-group">
                      <input
                        type="number"
                        placeholder="0"
                        className="workflow-input small"
                        value={newAction.offset_amount}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_amount: e.target.value,
                          })
                        }
                      />
                      <select
                        className="workflow-input small"
                        value={newAction.offset_unit}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_unit: e.target.value,
                          })
                        }
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                  </div>

                  <div className="sales-field">
                    <label>Timing Reference</label>
                    <select
                      value={newAction.offset_direction}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          offset_direction: e.target.value,
                        })
                      }
                    >
                      <option value="after_previous">
                        After Previous Action
                      </option>
                      <option value="after">After Trigger</option>
                      <option value="before">Before Trigger</option>
                    </select>
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Tag Name</label>
                    <select
                      value={newAction.tag}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          tag: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Tag</option>
                      {tagOptions.map((tag) => (
                        <option key={tag} value={tag}>
                          {tag}
                        </option>
                      ))}
                      <option value="create_new">+ Create Tag</option>
                    </select>
                  </div>
                </>
              )}

              {newAction.action_type === "update_status" && (
                <>
                  <div className="sales-field">
                    <label>Due In</label>
                    <div className="workflow-inline-group">
                      <input
                        type="number"
                        placeholder="0"
                        className="workflow-input small"
                        value={newAction.offset_amount}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_amount: e.target.value,
                          })
                        }
                      />
                      <select
                        className="workflow-input small"
                        value={newAction.offset_unit}
                        onChange={(e) =>
                          setNewAction({
                            ...newAction,
                            offset_unit: e.target.value,
                          })
                        }
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                  </div>

                  <div className="sales-field">
                    <label>Timing Reference</label>
                    <select
                      value={newAction.offset_direction}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          offset_direction: e.target.value,
                        })
                      }
                    >
                      <option value="after_previous">
                        After Previous Action
                      </option>
                      <option value="after">After Trigger</option>
                      <option value="before">Before Trigger</option>
                    </select>
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Select Status</label>
                    <select
                      value={newAction.status}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          status: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Status</option>
                      {leadStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {newAction.action_type && (
                <div className="workflow-action-footer">
                  <button
                    type="button"
                    className="sales-primary-btn"
                    onClick={handleAddActionInline}
                  >
                    {editingActionId ? "Update Action" : "Add Action"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: "12px" }}>
          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Workflow Actions</h2>
              <p>
                Review the step order, due timing, and action details
                for this workflow.
              </p>
            </div>

            {actions.length === 0 ? (
              <div className="workflow-empty-state">No actions added yet.</div>
            ) : (
              <div className="workflow-actions-list">
                {actions.map((action, index) => (
                  <div key={action.id} className="workflow-action-card">
                    <div className="workflow-action-top">
                      <div className="workflow-action-step">
                        Step {index + 1}
                      </div>

                      <div className="workflow-action-links">
                        <button
                          type="button"
                          className="workflow-link-btn"
                          onClick={() => handleEditAction(action)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="workflow-link-btn workflow-link-btn-danger"
                          onClick={() => handleDeleteAction(action.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="workflow-action-grid">
                      <div>
                        <div className="workflow-action-label">Action</div>
                        <div className="workflow-action-value">
                          {action.action_type.replaceAll("_", " ")}
                        </div>
                      </div>

                      <div>
                        <div className="workflow-action-label">Due In</div>
                        <div className="workflow-action-value">
                          {action.offset_amount
                            ? `${action.offset_amount} ${action.offset_unit}`
                            : "No delay"}
                        </div>
                      </div>

                      <div>
                        <div className="workflow-action-label">Timing</div>
                        <div className="workflow-action-value">
                          {action.offset_direction
                            ? action.offset_direction.replaceAll("_", " ")
                            : "—"}
                        </div>
                      </div>

                      <div className="workflow-action-span">
                        <div className="workflow-action-label">Details</div>
                        <div className="workflow-action-value">
                          {getActionSummary(action)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}